import React from 'react';
import { Check, Smartphone } from 'lucide-react';
import { Card, CardContent, CardHeader, CardFooter } from './ui/card';

const Pricing = () => {

  const plans = [
    {
      name: 'Free',
      bestFor: 'For home',
      features: [
        'Core language access (EN/PL)',
        '1 starter category',
        'Multiple child profiles',
        'Basic session tracking and rewards',
        'Practice on one device'
      ],
      cta: 'Start free in app',
      popular: false
    },
    {
      name: 'Individual Premium',
      bestFor: 'For one learner',
      features: [
        'Full access for one learner',
        'More languages and voices',
        'Full progress insights',
        'Smart repetition, rewards, and session tracking'
      ],
      cta: 'Available in app',
      popular: true
    },
    {
      name: 'Team Starter',
      bestFor: 'For small groups',
      features: [
        'Up to 10 learner device slots',
        'More learner slots',
        'Multiple devices',
        'One place to support practice outside class'
      ],
      cta: 'Available in app',
      popular: false
    },
    {
      name: 'Team Standard',
      bestFor: 'For schools',
      features: [
        'Up to 25 learner device slots',
        'Built for larger groups',
        'Manage practice across larger groups',
        'Export progress summary (CSV)|soon'
      ],
      cta: 'Available in app',
      popular: false
    }
  ];


  return (
    <section id="plans" className="py-8 sm:py-12 lg:py-16 px-4 sm:px-6 lg:px-8 bg-[#F5F5F5]">
      <div className="max-w-7xl mx-auto">
        {/* Section header */}
        <div className="text-left sm:text-center mb-8 sm:mb-16 space-y-4">
          <div className="inline-block px-4 py-2 bg-[#FFF3E0] rounded-full border border-[#FFE0B2]">
            <span className="text-[#FF7F30] font-semibold text-sm tracking-wide">Plans</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-[#333333] leading-[1.05] sm:leading-tight lg:leading-[1.1]">
            Choose the Plan That
            <span className="block text-[#FF7F30]">Fits Your Routine</span>
          </h2>
          <p className="max-w-2xl text-lg sm:text-xl mx-auto text-[#666666] leading-relaxed">
            From one child at home to multiple learners in a school setting. Choose the access level that matches your routine, with exact pricing shown in the App&nbsp;Store or Google&nbsp;Play.
          </p>
          <p className="max-w-2xl text-lg sm:text-xl mx-auto font-bold text-[#333333] leading-relaxed">
            Start small, upgrade when you need more learners, languages, or devices.
          </p>
        </div>

        {/* Pricing cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-y-6 sm:gap-y-12 gap-x-6 md:gap-y-8 lg:gap-6 pt-2">
          {plans.map((plan, index) =>
          <Card
            key={index}
            className={`relative flex flex-col rounded-2xl ${
            plan.popular ?
            'border-2 border-[#FF7F30] bg-white shadow-sm lg:scale-105 z-10' :
            'border-2 border-gray-200 bg-white shadow-none'}`
            }>

              {plan.popular &&
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-[#FF7F30] text-white px-4 py-1 rounded-full font-semibold text-sm">
                    Most Popular
                  </div>
                </div>
            }

              <CardHeader className="text-center pb-6 pt-8">
                <h3 className="text-2xl font-bold text-[#333333] mb-2">{plan.name}</h3>
                <div className="text-sm font-medium text-[#888888]">
                  {plan.bestFor}
                </div>
              </CardHeader>

              <CardContent className="space-y-4 pb-4 flex-grow">
                <ul className="space-y-3">
                  {plan.features.map((feature, idx) => {
                    const [featureText, badge] = feature.split('|');
                    return (
                      <li key={idx} className="flex items-start">
                        <Check size={20} className="text-[#4CAF50] mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-[#666666] text-sm leading-tight inline">
                          {featureText}{' '}
                          {badge === 'soon' && (
                            <span className="inline-block px-1.5 py-0.5 bg-[#FFA55A] text-white text-[10px] font-semibold rounded uppercase whitespace-nowrap align-middle">
                              Soon
                            </span>
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </CardContent>

              <CardFooter className="pt-4 pb-6 mt-auto">
                <p className="w-full text-center text-[#888888] text-sm italic">
                  {plan.cta}
                </p>
              </CardFooter>
            </Card>
          )}
        </div>

        {/* Note */}
        <div className="mt-6 sm:mt-12 px-4 text-left sm:text-center">
          <p className="text-[#666666] text-sm">
            All plans are annual. Pricing is managed through the App Store or Google Play and may vary by region. Device licensing is enforced automatically.
          </p>
        </div>

        {/* Download CTA Section - PRIMARY CTA */}
        <div className="mt-12 sm:mt-20 text-left sm:text-center" id="download">
          <div className="inline-block px-4 py-2 bg-[#FFF3E0] rounded-full border border-[#FFE0B2] mb-6">
            <span className="text-[#FF7F30] font-semibold text-sm tracking-wide">Get Started</span>
          </div>
          <h3 className="text-3xl sm:text-4xl font-bold text-[#333333] tracking-[-1px] mb-4">Ready to Start Swiping to Remember?</h3>
          <p className="text-[#666666] mb-10 max-w-3xl mx-auto text-lg leading-relaxed">
            Download LinguPix and turn a few minutes of daily practice into lasting vocabulary progress.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center sm:justify-center">
            <a 
              href="#" 
              className="transition-transform lg:hover:scale-105 duration-200"
              aria-label="Download on the App Store"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_a0c8a310-9195-420d-8ad2-c73f20567f76/artifacts/q4g0bwgs_apple-store-btn.png" 
                alt="Download on the App Store" 
                className="h-[56px] w-auto drop-shadow-sm"
              />
            </a>
            <a 
              href="#" 
              className="transition-transform lg:hover:scale-105 duration-200"
              aria-label="Get it on Google Play"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_a0c8a310-9195-420d-8ad2-c73f20567f76/artifacts/8sq6qmas_play-store-btn.png" 
                alt="Get it on Google Play" 
                className="h-[56px] w-auto drop-shadow-sm"
              />
            </a>
          </div>
        </div>
      </div>
    </section>);

};

export default Pricing;